/*
 * Auth context for the AquaStar admin console.
 *
 * State: { token, staff, status }
 *   - token comes from sessionStorage (apiClient owns the storage).
 *   - staff is the response from aquastarStaffMe — { userId, role, displayName, email }
 *   - status: 'unknown' | 'guest' | 'authed' | 'denied'
 *
 *     'unknown'  — booted, haven't pinged staffMe yet (show splash)
 *     'guest'    — no token (show login)
 *     'authed'   — token + staff row found (let them in)
 *     'denied'   — token works but no staff row (show "not authorized" page)
 *
 * The role gate is server-side: the aquastarStaffMe handler queries
 * app_aquastar_<stage>.aquastar_staff for the caller's user_id and 403s
 * when missing. We just reflect the answer in UI state.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  ApiError,
  getFn,
  getMe,
  getToken,
  login as apiLogin,
  logout as apiLogout,
  SESSION_EXPIRED_EVENT,
} from './apiClient';

/** 'vendor' (AQUASTAR-336, 2026-09-02): an outside supplier (Emaux) who may
 *  open the console but only sees the Firmware page. Not staff — every other
 *  page redirects a vendor to /firmware (App.tsx VendorScope). */
export type StaffRole = 'admin' | 'support' | 'sales' | 'vendor';

/**
 * Combined view of the signed-in user. Built by merging:
 *   - GET /auth/me                (Gateway-tier: identity, email)
 *   - aquastarStaffMe handler     (per-app: role, optional displayName override)
 *
 * Two endpoints, one merge: the Gateway owns identity, the app owns
 * authorization. Keeps AquaStar-staff concerns out of the platform-tier
 * users table.
 */
export interface AquastarStaff {
  userId: string;
  email: string;
  displayName: string;
  role: StaffRole;
  /** True when /auth/me reports users.is_platform_admin. Drives the
   *  /platform nav link in AdminLayout — non-platform-admin staff get
   *  the AquaStar console only. */
  isPlatformAdmin: boolean;
}

interface StaffMeResponse {
  userId: string;
  role: StaffRole;
  displayName: string | null;
  notes?: string | null;
}

export type AuthStatus = 'unknown' | 'guest' | 'authed' | 'denied';

interface AuthContextValue {
  status: AuthStatus;
  staff: AquastarStaff | null;
  error: string | null;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => void;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('unknown');
  const [staff, setStaff] = useState<AquastarStaff | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!getToken()) {
      setStaff(null);
      setStatus('guest');
      return;
    }
    try {
      // Two endpoints, run in parallel. /auth/me confirms the JWT is
      // still good AND gives us identity (email, displayName).
      // aquastarStaffMe gates the console (per-app role).
      const [me, staffRow] = await Promise.all([
        getMe(),
        getFn<StaffMeResponse>('aquastarStaffMe'),
      ]);

      const merged: AquastarStaff = {
        userId: staffRow.userId || me.userId,
        email: me.email,
        // staff_table.display_name wins if set (lets ops customize
        // how a staffer shows up in the console without touching
        // their Gateway profile); otherwise fall back to the
        // Gateway-tier displayName / givenName / email-local-part.
        displayName:
          staffRow.displayName ||
          me.displayName ||
          me.givenName ||
          (me.email ? me.email.split('@')[0] : 'Staff'),
        role: staffRow.role,
        isPlatformAdmin: me.isPlatformAdmin === true,
      };

      setStaff(merged);
      setStatus('authed');
      setError(null);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        // /auth/me succeeded (token is valid) but the user isn't on
        // aquastar_staff. We can still show a useful "denied" page —
        // try to fetch /auth/me alone for the email, ignore failures.
        let email: string | undefined;
        try {
          const me = await getMe();
          email = me.email;
        } catch {
          /* swallow — we'll show the generic denied page */
        }
        setStaff(
          email
            ? ({
                userId: '',
                email,
                displayName: email.split('@')[0],
                role: 'sales',
                isPlatformAdmin: false,
              } as AquastarStaff)
            : null,
        );
        setStatus('denied');
        setError('Your account is not authorized to use the admin console.');
        return;
      }
      if (err instanceof ApiError && err.status === 401) {
        // Token is bad/expired — apiClient already cleared it.
        setStaff(null);
        setStatus('guest');
        return;
      }
      // Network or unknown — leave as guest so they can retry.
      setStaff(null);
      setStatus('guest');
      setError(err instanceof Error ? err.message : 'Unable to verify session.');
    }
  }, []);

  // Boot — check existing session.
  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Session timeout: any 401 (token expired/invalid) dispatches this event from
  // the apiClient. Drop to 'guest' so the route guard redirects to /login; the
  // Login page reads the SESSION_EXPIRED flag and shows a friendly notice.
  useEffect(() => {
    const onExpired = () => {
      setStaff(null);
      setStatus('guest');
      setError(null);
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
  }, []);

  const signIn = useCallback(
    async (email: string, password: string) => {
      setError(null);
      try {
        await apiLogin(email, password);
        await refresh();
      } catch (err) {
        const msg =
          err instanceof Error ? err.message : 'Sign-in failed. Please try again.';
        setError(msg);
        throw err;
      }
    },
    [refresh],
  );

  const signOut = useCallback(() => {
    apiLogout();
    setStaff(null);
    setStatus('guest');
    setError(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ status, staff, error, signIn, signOut, refresh }),
    [status, staff, error, signIn, signOut, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
